import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type UIEvent } from 'react'
import { BellOff, ChevronDown, Hash, MessagesSquare, HeadphoneOff, MessageSquare, MicOff, Plus, Settings, Volume2, Zap } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { channelMuted, useGuildStore } from '@/store/guilds'
import { forumActivity } from '@/lib/forums'
import { ownMuted } from '@/lib/muting'
import { useSocialStore } from '@/store/social'
import { useVoiceStore } from '@/store/voice'
import { userCardHandler, userContextHandler } from '@/store/contextMenu'
import { guildApi } from '@/lib/guildApi'
import { Avatar } from './Avatar'
import { Tooltip } from './Tooltip'
import { avatarUrl } from '@/lib/avatar'
import type { VoiceState } from '@/types/api'
import { P, basePermissions, channelPermissions, has } from '@/lib/permissions'
import { ANIMATED_BANNER_TIER, BANNER_TIER, isUnread, memberName, threadsOf } from '@/lib/guildUi'
import { planMove, type DropTarget } from '@/lib/channelOrder'
import { ChannelType, type Channel, type Guild, type Member, type User } from '@/types/api'
import { cx } from '@/lib/cx'
import { GuildMenu } from './guild/GuildMenu'
import { GuildBanner } from './guild/GuildBanner'
import { CommunityEntry } from './guild/CommunityPanel'
import { BoostProgress } from './guild/BoostProgress'
import { CreateChannelModal } from './guild/CreateChannelModal'
import { ChannelSettingsModal } from './guild/ChannelSettingsModal'
import { ConfirmModal } from './guild/ConfirmModal'
import { CategoryContextMenu, ChannelContextMenu } from './guild/ChannelMenus'
import { LiveStream } from './voice/StreamCard'
import styles from './ChannelSidebar.module.css'
import { StyledName } from './StyledName'
import { EffectMark } from './voice/EffectMark'
import { publicUser } from '@/lib/auth'

type Drop = 'before' | 'after' | 'inside' | null

export function ChannelSidebar({ guildId }: { guildId: string }) {
  const view = useAppStore((s) => s.view)
  const setView = useAppStore((s) => s.setView)
  const setVoiceChatChannelId = useAppStore((s) => s.setVoiceChatChannelId)
  const collapsed = useAppStore((s) => s.collapsed)
  const toggleCategory = useAppStore((s) => s.toggleCategory)
  const me = useAuthStore((s) => s.user)
  const guild = useGuildStore((s) => s.guilds[guildId])
  const channelMap = useGuildStore((s) => s.channels[guildId])
  const member = useGuildStore((s) => (me ? s.members[guildId]?.[me.id] : undefined))
  const readStates = useSocialStore((s) => s.readStates)
  const settings = useGuildStore((s) => s.settings[guildId])
  const reorderChannels = useGuildStore((s) => s.reorderChannels)
  const deleteChannel = useGuildStore((s) => s.deleteChannel)
  const voiceStates = useGuildStore((s) => s.voiceStates[guildId])
  const members = useGuildStore((s) => s.members[guildId])
  const ensureMembers = useGuildStore((s) => s.ensureMembers)
  const speaking = useVoiceStore((s) => s.speaking)
  const joinVoice = useVoiceStore((s) => s.join)
  const [menuOpen, setMenuOpen] = useState(false)
  const [context, setContext] = useState<{ channel: Channel; x: number; y: number } | null>(null)
  const [categoryMenu, setCategoryMenu] = useState<{ category: Channel; items: Channel[]; x: number; y: number } | null>(null)
  const [creating, setCreating] = useState<{ parentId: string | null; type: number } | null>(null)
  const [editing, setEditing] = useState<Channel | null>(null)
  const [deletingCategory, setDeletingCategory] = useState<Channel | null>(null)
  // перетаскивание: что тащим и над чем держим
  const [drag, setDrag] = useState<{ id: string; category: boolean } | null>(null)
  const [over, setOver] = useState<DropTarget | null>(null)
  // перенос человека из голосового канала в другой (MOVE_MEMBERS), как в Discord
  const [voiceDrag, setVoiceDrag] = useState<{ userId: string; from: string } | null>(null)
  const [voiceOver, setVoiceOver] = useState<string | null>(null)
  // баннер уехал под название сервера — шапке нужен свой фон
  const [pastBanner, setPastBanner] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)

  const self: Member | undefined = useMemo(() => member ?? (me ? { user: publicUser(me), nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null } : undefined), [member, me])
  const base = guild && self ? basePermissions(guild, self) : 0n
  const canManage = has(base, P.MANAGE_CHANNELS)
  // баннер виден со 2-го уровня: уровень упал — картинка остаётся в настройках, но над каналами не стоит
  const banner = guild?.banner && guild.premium_tier >= BANNER_TIER ? guild.banner : null
  const onChannelsScroll = (e: UIEvent<HTMLDivElement>) => {
    const bannerHeight = (e.currentTarget.firstElementChild as HTMLElement | null)?.offsetHeight ?? 0
    setPastBanner(e.currentTarget.scrollTop > bannerHeight - (headerRef.current?.offsetHeight ?? 48))
  }

  const tree = useMemo(() => buildTree(guild, self, channelMap ? Object.values(channelMap) : [], canManage), [guild, self, channelMap, canManage])
  const viewGuildId = view.kind === 'guild' ? view.guildId : null
  const selectedId = view.kind === 'guild' ? view.channelId : null

  // кого в голосе нечем назвать — дозапросить: на телефоне список участников справа закрыт,
  // и без этого вместо ников стояли id
  const unknownVoice = useMemo(
    () => Object.values(voiceStates ?? {}).filter((vs) => !members?.[vs.user_id] && !vs.member).map((vs) => vs.user_id).sort().join(','),
    [voiceStates, members],
  )
  useEffect(() => {
    if (unknownVoice) void ensureMembers(guildId, unknownVoice.split(','))
  }, [guildId, unknownVoice, ensureMembers])

  // без выбранного канала открываем первый текстовый
  useEffect(() => {
    if (viewGuildId !== guildId) return
    if (!channelMap || Object.keys(channelMap).length === 0) return // каналы ещё не пришли
    if (selectedId && channelMap[selectedId]) return
    const first = tree.flatMap((g) => g.channels).find((c) => (c.type === ChannelType.GuildText || c.type === ChannelType.GuildForum))
    // автовыбор — не тап человека: на телефоне список каналов должен остаться на экране
    if (first) setView({ kind: 'guild', guildId, channelId: first.id }, { keepPane: true })
  }, [viewGuildId, guildId, selectedId, channelMap, tree, setView])

  const endDrag = () => {
    setDrag(null)
    setOver(null)
  }
  const startDrag = (ch: Channel) => (e: DragEvent<HTMLElement>) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', ch.id)
    setDrag({ id: ch.id, category: ch.type === ChannelType.GuildCategory })
  }
  const setTarget = (t: DropTarget) =>
    setOver((o) => (o && JSON.stringify(o) === JSON.stringify(t) ? o : t))
  const half = (e: DragEvent<HTMLElement>): 'before' | 'after' => {
    const rect = e.currentTarget.getBoundingClientRect()
    return e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
  }
  const overChannel = (ch: Channel) => (e: DragEvent<HTMLElement>) => {
    if (!drag || drag.category || drag.id === ch.id) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setTarget({ kind: 'channel', id: ch.id, where: half(e) })
  }
  const overCategory = (cat: Channel) => (e: DragEvent<HTMLElement>) => {
    if (!drag || drag.id === cat.id) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setTarget(drag.category ? { kind: 'category', id: cat.id, where: half(e) } : { kind: 'category', id: cat.id, where: 'inside' })
  }
  const overRoot = (e: DragEvent<HTMLElement>) => {
    if (!drag || drag.category) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setTarget({ kind: 'root' })
  }
  const drop = (target: DropTarget) => (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    const dragged = drag
    endDrag()
    if (!dragged) return
    const items = planMove(Object.values(channelMap ?? {}), dragged.id, target)
    if (items) void reorderChannels(guildId, items).catch(() => undefined)
  }
  const dropFor = (kind: 'channel' | 'category', id: string): Drop => (over && over.kind === kind && over.id === id ? over.where : null)

  const perm = (ch: Channel | undefined, p: bigint) => Boolean(guild && self && ch && has(channelPermissions(guild, self, ch), p))
  // тащить можно с правом переносить в том канале, где человек сидит: себя (это обычный переход)
  // и других, кроме владельца сервера и музыкального бота — он за переносом не следит
  const canDragVoice = (vs: VoiceState, user: User | undefined) =>
    perm(channelMap?.[vs.channel_id ?? ''], P.MOVE_MEMBERS) && (vs.user_id === me?.id || (vs.user_id !== guild?.owner_id && !user?.bot))
  const endVoiceDrag = () => {
    setVoiceDrag(null)
    setVoiceOver(null)
  }
  // человек вышел или его перенесли, пока тащили: его строка пропала, и dragend уже не придёт —
  // сбрасываем сами, иначе список так и остался бы в режиме перетаскивания
  if (voiceDrag && voiceStates?.[voiceDrag.userId]?.channel_id !== voiceDrag.from) endVoiceDrag()
  // бросить можно в другой голосовой канал, куда можно самому (так проверяет и сервер)
  const dragOverVoice = (ch: Channel) => (e: DragEvent<HTMLElement>) => {
    if (!voiceDrag || ch.type !== ChannelType.GuildVoice || ch.id === voiceDrag.from || !perm(ch, P.CONNECT)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setVoiceOver(ch.id)
  }
  const dragLeaveVoice = (ch: Channel) => (e: DragEvent<HTMLElement>) => {
    if (voiceOver === ch.id && !e.currentTarget.contains(e.relatedTarget as Node | null)) setVoiceOver(null)
  }
  const dropOnVoice = (ch: Channel) => (e: DragEvent<HTMLElement>) => {
    e.preventDefault()
    const dragged = voiceDrag
    endVoiceDrag()
    if (!dragged || dragged.from === ch.id) return
    // себя — обычным входом; другого — переносом, у него клиент сам уйдёт в новую комнату.
    // Ошибку не показываем, как и при перетаскивании каналов: человек просто остаётся на месте
    if (dragged.userId === me?.id) void joinVoice(guildId, ch.id).catch(() => undefined)
    else void guildApi.updateMember(guildId, dragged.userId, { channel_id: ch.id }).catch(() => undefined)
  }
  const closeContext = useCallback(() => setContext(null), [])
  const closeCategoryMenu = useCallback(() => setCategoryMenu(null), [])

  if (!guild) return <aside className={styles.sidebar} aria-label="Каналы" />

  // канал и его активные ветки; в свёрнутой категории остаются только выбранные и непрочитанные
  const item = (ch: Channel, collapsedGroup = false) => {
    const selected = ch.id === selectedId
    // серым — и когда заглушена категория или весь сервер; колокольчик — только у своего mute
    const muted = channelMuted(settings, ch.id, channelMap)
    const forum = ch.type === ChannelType.GuildForum ? forumActivity(ch, channelMap ?? {}, readStates, settings) : null
    const unread = forum?.unread ?? (isUnread(ch, readStates[ch.id]) && !muted)
    const showRow = !collapsedGroup || selected || unread || Boolean(drag)
    const threads = ch.type === ChannelType.GuildText ? threadsOf(channelMap, ch.id) : []
    const voice = ch.type === ChannelType.GuildVoice ? Object.values(voiceStates ?? {}).filter((v) => v.channel_id === ch.id) : []
    // пока тащат человека, строки канала и его участников — одна цель броска
    const voiceTarget = voiceDrag ? { onDragOver: dragOverVoice(ch), onDragLeave: dragLeaveVoice(ch), onDrop: dropOnVoice(ch) } : null
    return (
      <Fragment key={ch.id}>
        {showRow ? (
          <ChannelItem
            channel={ch}
            selected={selected}
            unread={unread}
            muted={muted}
            bell={ownMuted(settings, ch.id)}
            mentions={forum?.mentions ?? readStates[ch.id]?.mention_count ?? 0}
            voiceCount={voice.length}
            canManage={canManage}
            dragging={drag?.id === ch.id}
            drop={dropFor('channel', ch.id)}
            voiceDrop={voiceOver === ch.id}
            onOpen={() => {
              setView({ kind: 'guild', guildId, channelId: ch.id })
              if (ch.type === ChannelType.GuildVoice && self && guild && has(channelPermissions(guild, self, ch), P.CONNECT)) void joinVoice(guildId, ch.id).catch(() => undefined)
            }}
            onSettings={() => setEditing(ch)}
            onOpenChat={() => {
              useAppStore.setState({ searchQuery: null, threadsPanel: null })
              setVoiceChatChannelId(ch.id)
              setView({ kind: 'guild', guildId, channelId: ch.id })
            }}
            onContext={(x, y) => setContext({ channel: ch, x, y })}
            onDragStart={startDrag(ch)}
            onDragOver={voiceTarget?.onDragOver ?? overChannel(ch)}
            onDragLeave={voiceTarget?.onDragLeave}
            onDrop={voiceTarget?.onDrop ?? ((e) => {
              const target = over?.kind === 'channel' && over.id === ch.id ? over : { kind: 'channel' as const, id: ch.id, where: half(e) }
              drop(target)(e)
            })}
            onDragEnd={endDrag}
          />
        ) : null}
        {voice.map((vs) => {
          const user = (members?.[vs.user_id] ?? vs.member)?.user
          const draggable = canDragVoice(vs, user)
          return (
            <VoiceUser
              key={vs.user_id}
              state={vs}
              name={members?.[vs.user_id] ? memberName(members[vs.user_id]!) : vs.member ? memberName(vs.member) : vs.user_id}
              avatar={avatarUrl(vs.user_id, user?.avatar)}
              speaking={Boolean(speaking[vs.user_id])}
              user={user}
              guildId={guildId}
              dragging={voiceDrag?.userId === vs.user_id}
              dropTarget={voiceOver === ch.id}
              onDragStart={draggable ? (e) => {
                e.stopPropagation()
                e.dataTransfer.effectAllowed = 'move'
                // свой тип, а не text/plain: брошенный в поле ввода, он не вставит туда id
                e.dataTransfer.setData('application/x-klichat-voice-member', vs.user_id)
                setVoiceDrag({ userId: vs.user_id, from: ch.id })
              } : undefined}
              onDragEnd={endVoiceDrag}
              {...(voiceTarget ?? {})}
            />
          )
        })}
        {threads.map((th) => {
          const tSelected = th.id === selectedId
          const tUnread = isUnread(th, readStates[th.id]) && !channelMuted(settings, th.id, channelMap)
          if ((collapsedGroup || !showRow) && !tSelected && !tUnread) return null
          return <ThreadItem key={th.id} thread={th} selected={tSelected} unread={tUnread} mentions={readStates[th.id]?.mention_count ?? 0} onOpen={() => setView({ kind: 'guild', guildId, channelId: th.id })} />
        })}
      </Fragment>
    )
  }

  return (
    <aside className={cx(styles.sidebar, banner && styles.withBanner)} aria-label="Каналы">
      <div ref={headerRef} className={cx(styles.headerWrap, banner && pastBanner && styles.pastBanner)}>
        <button type="button" className={styles.header} data-tooltip={guild.name} aria-haspopup="menu" aria-expanded={menuOpen} onMouseDown={(e) => e.stopPropagation()} onClick={() => setMenuOpen((v) => !v)}>
          <span className={styles.guildName}>{guild.name}</span>
          {/* уровень сервера виден там, где на него смотрят; с полоской бустов под баннером он уже на ней */}
          {guild.premium_tier > 0 && !guild.premium_progress_bar_enabled ? (
            <span className={styles.boost} data-tooltip={`Уровень ${guild.premium_tier}: сервер забустили`}>
              <Zap size={11} />{guild.premium_tier}
            </span>
          ) : null}
          <ChevronDown size={20} />
        </button>
        {menuOpen ? <GuildMenu guild={guild} perms={base} onClose={() => setMenuOpen(false)} onCreateChannel={(type) => setCreating({ parentId: null, type })} /> : null}
      </div>
      <div className={cx(styles.channels, 'scroller', (drag || voiceDrag) && styles.dragging)} onScroll={banner ? onChannelsScroll : undefined}>
        {banner ? <GuildBanner key={banner} guildId={guildId} hash={banner} animate={guild.premium_tier >= ANIMATED_BANNER_TIER} /> : null}
        {guild.premium_progress_bar_enabled ? <BoostProgress guild={guild} /> : null}
        {guild.features.some(f => ['GAME_HUB_DOTA2', 'GAME_HUB_CS2', 'GAME_HUB_DEADLOCK', 'GAME_HUB_MINECRAFT', 'GAME_HUB_GTA5RP', 'GAME_HUB_ROBLOX'].includes(f)) ? <CommunityEntry key={guildId} guildId={guildId} /> : null}
        {tree.map((group) => {
          if (!group.category) return group.channels.map((ch) => item(ch))
          const cat = group.category
          const isCollapsed = Boolean(collapsed[cat.id])
          const catDrop = dropFor('category', cat.id)
          const catMuted = channelMuted(settings, cat.id)
          return (
            <section key={cat.id} className={styles.category} aria-label={cat.name ?? ''}>
              <div
                className={cx(styles.categoryRow, catMuted && styles.categoryMuted, drag?.id === cat.id && styles.rowDragging, catDrop === 'before' && styles.dropBefore, catDrop === 'after' && styles.dropAfter, catDrop === 'inside' && styles.dropInside)}
                onContextMenu={(e) => {
                  e.preventDefault()
                  setCategoryMenu({ category: cat, items: group.channels, x: e.clientX, y: e.clientY })
                }}
                draggable={canManage}
                onDragStart={canManage ? startDrag(cat) : undefined}
                onDragOver={canManage ? overCategory(cat) : undefined}
                onDrop={canManage ? (e) => drop(over?.kind === 'category' && over.id === cat.id ? over : { kind: 'category', id: cat.id, where: 'inside' })(e) : undefined}
                onDragEnd={endDrag}
              >
                <button type="button" className={styles.categoryToggle} onClick={() => toggleCategory(cat.id)} aria-expanded={!isCollapsed}>
                  <ChevronDown size={12} className={cx(styles.chevron, isCollapsed && styles.chevronCollapsed)} />
                  {cat.name}
                  {ownMuted(settings, cat.id) ? <BellOff size={12} className={styles.mutedIcon} aria-label="Категория заглушена" /> : null}
                </button>
                {canManage ? (
                  <button type="button" className={styles.categoryAdd} data-tooltip="Создать канал" aria-label={`Создать канал в ${cat.name}`} onClick={() => setCreating({ parentId: cat.id, type: ChannelType.GuildText })}>
                    <Plus size={18} />
                  </button>
                ) : null}
              </div>
              {group.channels.map((ch) => item(ch, isCollapsed))}
            </section>
          )
        })}
        {tree.length === 0 ? <p className={styles.empty}>Каналов пока нет.</p> : null}
        {drag && !drag.category ? (
          <div className={cx(styles.dropRoot, over?.kind === 'root' && styles.dropRootOn)} onDragOver={overRoot} onDrop={drop({ kind: 'root' })}>
            Вне категорий
          </div>
        ) : null}
      </div>
      {context ? (
        <ChannelContextMenu
          guild={guild}
          channel={context.channel}
          x={context.x}
          y={context.y}
          canManage={canManage}
          onClose={closeContext}
          onSettings={() => { setEditing(context.channel); setContext(null) }}
        />
      ) : null}
      {categoryMenu ? (
        <CategoryContextMenu
          guild={guild}
          category={categoryMenu.category}
          items={categoryMenu.items}
          x={categoryMenu.x}
          y={categoryMenu.y}
          canManage={Boolean(self && has(channelPermissions(guild, self, categoryMenu.category), P.MANAGE_CHANNELS))}
          onSettings={() => { setEditing(categoryMenu.category); setCategoryMenu(null) }}
          onDelete={() => { setDeletingCategory(categoryMenu.category); setCategoryMenu(null) }}
          onClose={closeCategoryMenu}
        />
      ) : null}
      {deletingCategory ? (
        <ConfirmModal
          title={`Удалить категорию «${deletingCategory.name ?? ''}»`}
          text="Каналы категории останутся на сервере без категории."
          confirmLabel="Удалить"
          onCancel={() => setDeletingCategory(null)}
          onConfirm={async () => {
            await deleteChannel(deletingCategory.id)
            setDeletingCategory(null)
          }}
        />
      ) : null}
      {creating ? <CreateChannelModal guildId={guildId} parentId={creating.parentId} initialType={creating.type} onClose={() => setCreating(null)} /> : null}
      {editing ? <ChannelSettingsModal guild={guild} channel={editing} perms={base} onClose={() => setEditing(null)} /> : null}
    </aside>
  )
}

interface Group {
  category: Channel | null
  channels: Channel[]
}

/** Каналы без категории, затем категории с видимыми детьми; невидимые по правам — скрыты. */
function buildTree(guild: Guild | undefined, self: Member | undefined, all: Channel[], canManage: boolean): Group[] {
  if (!guild || !self) return []
  const visible = (ch: Channel) => has(channelPermissions(guild, self, ch), P.VIEW_CHANNEL)
  const byPos = (a: Channel, b: Channel) => (a.position ?? 0) - (b.position ?? 0) || (a.id < b.id ? -1 : 1)
  const categories = all.filter((c) => c.type === ChannelType.GuildCategory).sort(byPos)
  const catIds = new Set(categories.map((c) => c.id))
  const plain = (c: Channel) => c.type !== ChannelType.GuildCategory && c.type !== ChannelType.PublicThread
  const loose = all.filter((c) => plain(c) && (!c.parent_id || !catIds.has(c.parent_id)) && visible(c)).sort(byPos)
  const out: Group[] = []
  if (loose.length) out.push({ category: null, channels: loose })
  for (const cat of categories) {
    const children = all.filter((c) => c.parent_id === cat.id && plain(c) && visible(c)).sort(byPos)
    if (children.length || canManage) out.push({ category: cat, channels: children })
  }
  return out
}

interface ItemProps {
  channel: Channel
  selected: boolean
  unread: boolean
  /** Заглушён сам, категорией или сервером — строка серая. */
  muted: boolean
  /** Заглушён именно этот канал — рядом колокольчик. */
  bell: boolean
  mentions: number
  voiceCount?: number
  canManage: boolean
  dragging: boolean
  drop: Drop
  /** Над голосовым каналом держат человека, которого переносят. */
  voiceDrop: boolean
  onOpen: () => void
  onSettings: () => void
  onOpenChat: () => void
  onContext: (x: number, y: number) => void
  onDragStart: (e: DragEvent<HTMLElement>) => void
  onDragOver: (e: DragEvent<HTMLElement>) => void
  onDragLeave?: ((e: DragEvent<HTMLElement>) => void) | undefined
  onDrop: (e: DragEvent<HTMLElement>) => void
  onDragEnd: () => void
}

function ChannelItem({ channel, selected, unread, muted, bell, mentions, voiceCount = 0, canManage, dragging, drop, voiceDrop, onOpen, onSettings, onOpenChat, onContext, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd }: ItemProps) {
  const voice = channel.type === ChannelType.GuildVoice
  const Icon = voice ? Volume2 : channel.type === ChannelType.GuildForum ? MessagesSquare : Hash
  return (
    <div
      className={cx(styles.channelRow, selected && styles.channelRowSelected, muted && styles.channelRowMuted, dragging && styles.rowDragging, drop === 'before' && styles.dropBefore, drop === 'after' && styles.dropAfter, voiceDrop && styles.dropInside)}
      data-channel-id={channel.id}
      data-manage={canManage ? '' : undefined}
      data-actions={voice ? (canManage ? '2' : '1') : canManage ? '1' : undefined}
      draggable={canManage}
      onDragStart={canManage ? onDragStart : undefined}
      // каналы переставляет тот, кто ими управляет, а людей в голосовой канал бросает и модератор без этого права
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <button
        type="button"
        className={styles.channel}
        data-selected={selected ? '' : undefined}
        data-unread={unread ? '' : undefined}
        aria-current={selected ? 'page' : undefined}
        onClick={onOpen}
        onContextMenu={(e) => {
          e.preventDefault()
          onContext(e.clientX, e.clientY)
        }}
      >
        <Icon size={20} className={styles.channelIcon} />
        <span className={styles.channelName}>
          {channel.name}
          {voice && (voiceCount || channel.user_limit) ? <span className={styles.limit}> · {voiceCount}{channel.user_limit ? `/${channel.user_limit}` : ''}</span> : null}
        </span>
        {bell ? <BellOff size={14} className={styles.mutedIcon} aria-label="Канал заглушен" /> : null}
        {mentions ? <span className={styles.mentions}>{mentions}</span> : null}
      </button>
      {voice || canManage ? (
        <div className={styles.channelActions}>
          {voice ? (
            <Tooltip content="Открыть чат" interactive>
              <button type="button" className={styles.channelChat} aria-label={`Открыть чат канала ${channel.name ?? ''}`} onClick={onOpenChat}><MessageSquare size={16} /></button>
            </Tooltip>
          ) : null}
          {canManage ? (
            <Tooltip content="Настроить канал" interactive>
              <button type="button" className={styles.channelSettings} aria-label={`Настройки канала ${channel.name ?? ''}`} onClick={onSettings}><Settings size={16} /></button>
            </Tooltip>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

interface VoiceUserProps {
  state: VoiceState
  name: string
  avatar?: string | undefined
  speaking: boolean
  user?: User | undefined
  guildId: string
  /** Этого человека сейчас переносят. */
  dragging: boolean
  /** Его канал — цель броска: подсвечиваем вместе со строкой канала. */
  dropTarget: boolean
  /** Есть — строку можно тащить в другой голосовой канал. */
  onDragStart?: ((e: DragEvent<HTMLElement>) => void) | undefined
  onDragEnd: () => void
  onDragOver?: ((e: DragEvent<HTMLElement>) => void) | undefined
  onDragLeave?: ((e: DragEvent<HTMLElement>) => void) | undefined
  onDrop?: ((e: DragEvent<HTMLElement>) => void) | undefined
}

/** Участник голосового канала под его строкой: кольцо речи, микрофон, звук, экран. */
function VoiceUser({ state, name, avatar, speaking, user, guildId, dragging, dropTarget, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop }: VoiceUserProps) {
  // та же карточка и то же меню, что у участника в списке справа: громкость, профиль, сообщение
  return (
    <div
      className={cx(styles.voiceUser, dragging && styles.rowDragging, dropTarget && styles.dropInside)}
      data-user-id={state.user_id}
      data-tooltip={name}
      onClick={userCardHandler(state.user_id, { user, guildId })}
      onContextMenu={userContextHandler(state.user_id, { user, guildId })}
      draggable={Boolean(onDragStart)}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(state.user_id)} size={24} speaking={speaking} src={avatar} decoration={user?.avatar_decoration_data} />
      <StyledName name={name} style={user?.display_name_styles} className={styles.voiceName} />
      <EffectMark userId={state.user_id} />
      {/* «В ЭФИРЕ» — правее значков, как у Discord: плашки встают в одну колонку по правому краю */}
      <span className={styles.voiceIcons}>
        {state.afk || state.mute || state.self_mute ? <MicOff size={14} className={state.mute ? styles.voiceServer : undefined} aria-label="Микрофон выключен" /> : null}
        {state.afk || state.deaf || state.self_deaf ? <HeadphoneOff size={14} className={state.deaf ? styles.voiceServer : undefined} aria-label="Звук выключен" /> : null}
        {state.self_stream && state.channel_id ? <LiveStream guildId={guildId} channelId={state.channel_id} userId={state.user_id} name={name} avatar={avatar} decoration={user?.avatar_decoration_data} color={colorOf(state.user_id)} /> : null}
      </span>
    </div>
  )
}

/** Активная ветка под своим каналом. */
function ThreadItem({ thread, selected, unread, mentions, onOpen }: { thread: Channel; selected: boolean; unread: boolean; mentions: number; onOpen: () => void }) {
  return (
    <div className={cx(styles.channelRow, styles.threadRow, selected && styles.channelRowSelected)} data-thread-id={thread.id}>
      <button
        type="button"
        className={cx(styles.channel, styles.thread)}
        data-selected={selected ? '' : undefined}
        data-unread={unread ? '' : undefined}
        aria-current={selected ? 'page' : undefined}
        data-tooltip={`Ветка ${thread.name ?? ''}`}
        onClick={onOpen}
      >
        <MessageSquare size={16} className={styles.channelIcon} />
        <span className={styles.channelName}>{thread.name}</span>
        {mentions ? <span className={styles.mentions}>{mentions}</span> : null}
      </button>
    </div>
  )
}
